import React, { useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { longDateString } from "../../utils";
import "../../starek.css";

function PartsListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const isPartSelected = (partId) => {
        return selectedRows.some((row) => row.id === partId);
    };

    const handleCheckboxChange = (part) => {
        let newSelectedRows;
        if (isPartSelected(part.id)) {
            newSelectedRows = selectedRows.filter((row) => row.id !== part.id);
        } else {
            newSelectedRows = [...selectedRows, part];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const currentPageItems = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredItems.slice(start, start + rowsPerPage);
    }, [filteredItems, currentPage, rowsPerPage]);

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    onChange={(e) => {
                        if (e.target.checked) {
                            const newRows = [
                                ...selectedRows,
                                ...currentPageItems.filter(
                                    (r) => !isPartSelected(r.id),
                                ),
                            ];
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({ selectedRows: newRows });
                            }
                        } else {
                            const pageIds = new Set(
                                currentPageItems.map((r) => r.id),
                            );
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({
                                    selectedRows: selectedRows.filter(
                                        (r) => !pageIds.has(r.id),
                                    ),
                                });
                            }
                        }
                    }}
                    checked={
                        currentPageItems.length > 0 &&
                        currentPageItems.every((row) => isPartSelected(row.id))
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = currentPageItems.some((r) =>
                            isPartSelected(r.id),
                        );
                        const all =
                            currentPageItems.length > 0 &&
                            currentPageItems.every((r) => isPartSelected(r.id));
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={isPartSelected(row.id)}
                    onChange={() => handleCheckboxChange(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Image",
            width: "80px",
            center: true,
            sortable: false,
            cell: (row) => (
                <div className="roundel roundel-md">
                    {row.image_asset?.url ? (
                        <img
                            src={row.image_asset.url}
                            alt={row.name || "Part"}
                        />
                    ) : (
                        <i className="fa fa-cube fa-lg text-muted"></i>
                    )}
                </div>
            ),
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            grow: 2,
        },
        {
            name: "Activity",
            selector: (row) => row.updated_at,
            sortable: true,
            width: "250px",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <div>
                                Created by {row.created_by?.name || "Unknown"}{" "}
                                at {longDateString(row.created_at)}
                            </div>
                            <div>
                                Modified by {row.updated_by?.name || "Unknown"}{" "}
                                at {longDateString(row.updated_at)}
                            </div>
                        </Tooltip>
                    }
                >
                    <div style={{ fontSize: "0.85rem" }}>
                        {row.updated_by?.name || "Unknown"} at{" "}
                        {longDateString(row.updated_at)}
                    </div>
                </OverlayTrigger>
            ),
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredItems}
            highlightOnHover
            striped
            responsive
            pagination
            dense
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newPerPage, page) => {
                setRowsPerPage(newPerPage);
                setCurrentPage(page);
            }}
        />
    );
}

export default PartsListingView;
